/**
 * Centralized Chart Data Service
 * 
 * Provides a unified interface for fetching all chart data using org-scoped NATS requests.
 * Eliminates the need for individual chart components to handle NATS communication directly.
 */

import { orgNatsClient, StatementIds } from './OrgNATSClient';
import type { NATSResponse } from './OrgNATSClient';

export interface ChartRequestParams {
  serialno?: string; // Machine serial number (for machine-level data)
  line_id?: string; // Line ID (for line-level aggregated data)
  date_from: string;
  date_to: string;
  shift_name: string[];
  agg_type?: 'shift' | 'day' | 'week';
  [key: string]: any; // Allow additional parameters
}

export class ChartDataService {
  private static readonly DEFAULT_TIMEOUT = 15000;

  /**
   * Generic chart data fetch method
   */
  private static async fetchChartData<T = any>(
    statementId: string,
    params: ChartRequestParams,
    token: string,
    timeout?: number
  ): Promise<NATSResponse<T>> {
    return orgNatsClient.request<T>(
      statementId,
      params,
      token,
      { timeout: timeout ?? ChartDataService.DEFAULT_TIMEOUT }
    );
  }

  /**
   * Fetch all line KPI data in a single request
   * Returns efficiency, utilization, output, downtime, mtbf, reject_ratio, number_of_faults
   */
  static async fetchLineKPIData(params: { line_id: string; date_from: string; date_to: string; shift_name: string[] }, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.LINE_KPI_DATA, params as ChartRequestParams, token);
  }

  /**
   * Build request params for line-level per-KPI requests. Line statements use
   * `line_bk` (not `serialno`/`line_id`) and do NOT take an `agg_type` param —
   * the aggregation level is encoded in the statement name instead.
   */
  static buildLineRequestParams(
    lineId: string,
    dateRange: { start: string; end: string },
    shifts: string[],
  ): Record<string, any> {
    return {
      line_bk: lineId,
      date_from: `${dateRange.start}T00:00:00.000Z`,
      date_to: `${dateRange.end}T23:59:59.999Z`,
      shift_name: shifts,
    };
  }

  /**
   * Fetch line-level output. The aggregation level selects the statement:
   *  - 'shift' → PC.line_output
   *  - 'day'   → PC.line_output_daily
   *  - 'week'  → PC.line_output_weekly
   * Response shape matches machine output: { results: { shift: { timestamp: value } } }.
   */
  static async fetchLineOutput(
    params: { line_bk: string; date_from: string; date_to: string; shift_name: string[] },
    aggType: 'shift' | 'day' | 'week',
    token: string,
  ): Promise<NATSResponse> {
    const statement =
      aggType === 'week'
        ? StatementIds.LINE_OUTPUT_WEEKLY
        : aggType === 'day'
          ? StatementIds.LINE_OUTPUT_DAILY
          : StatementIds.LINE_OUTPUT;
    return this.fetchChartData(statement, params as unknown as ChartRequestParams, token);
  }

  /**
   * Fetch line-level efficiency + utilization (returns one row per machine per
   * shift: { shift_date, shift_name, machine_sn, machine_name, efficiency, utilization }).
   * Uses the `line_bk` parameter (like line available shifts).
   */
  static async fetchLineEfficiency(params: { line_bk: string; date_from: string; date_to: string; shift_name: string[] }, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.LINE_EFFICIENCY, params as unknown as ChartRequestParams, token);
  }

  /**
   * Fetch efficiency data (machine-level only)
   */
  static async fetchEfficiency(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.EFFICIENCY, params, token);
  }

  /**
   * Fetch utilization data (machine-level only)
   */
  static async fetchUtilization(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.UTILIZATION, params, token);
  }

  /**
   * Fetch efficiency and utilization together (parallel)
   */
  static async fetchEfficiencyAndUtilization(params: ChartRequestParams, token: string): Promise<{
    efficiency: NATSResponse;
    utilization: NATSResponse;
  }> {
    const [efficiency, utilization] = await Promise.all([
      this.fetchEfficiency(params, token),
      this.fetchUtilization(params, token)
    ]);

    return { efficiency, utilization };
  }

  /**
   * Fetch output data (machine-level only)
   */
  static async fetchOutput(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.OUTPUT, params, token);
  }

  /**
   * Fetch downtime accumulated data (machine-level only)
   */
  static async fetchDowntime(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.DOWNTIME_ACCUMULATED, params, token);
  }

  /**
   * Fetch downtime duration of faults (machine-level only)
   */
  static async fetchDowntimeDurationOfFaults(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.DOWNTIME_DURATION_OF_FAULTS, params, token);
  }

  /**
   * Fetch machine status per shift
   */
  static async fetchMachineStatusPerShift(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.MACHINE_STATUS_PER_SHIFT, params, token);
  }

  /**
   * Fetch MTBF data (machine-level only)
   */
  static async fetchMTBF(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.MTBF, params, token);
  }

  /**
   * Fetch average production speed
   */
  static async fetchAverageProductionSpeed(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.AVERAGE_PRODUCTION_SPEED, params, token);
  }

  /**
   * Fetch reject ratio (machine-level only)
   */
  static async fetchRejectRatio(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.REJECT_RATIO, params, token);
  }

  /**
   * Fetch number of rejects (machine-level only)
   */
  static async fetchNumberOfRejects(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.NUMBER_OF_REJECTS, params, token);
  }

  /**
   * Fetch number of reject events
   */
  static async fetchNumberOfRejectEvents(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.NUMBER_OF_REJECT_EVENTS, params, token);
  }

  /**
   * Fetch number of faults (machine-level only)
   */
  static async fetchNumberOfFaults(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.NUMBER_OF_FAULTS, params, token);
  }

  /**
   * Fetch presets
   */
  static async fetchPresets(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.PRESETS, params, token);
  }

  /**
   * Fetch preset details
   */
  static async fetchPresetDetails(params: ChartRequestParams, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.PRESETS_DETAIL, params, token);
  }

  /**
   * Fetch available dates
   */
  static async fetchAvailableDates(params: Pick<ChartRequestParams, 'serialno'>, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.AVAILABLE_DATES, params as ChartRequestParams, token);
  }

  /**
   * Fetch available shifts
   */
  static async fetchAvailableShifts(params: Pick<ChartRequestParams, 'serialno' | 'date_from' | 'date_to'>, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.AVAILABLE_SHIFTS, params as ChartRequestParams, token);
  }

  /**
   * Fetch available shifts for a line (uses line_bk parameter)
   */
  static async fetchLineAvailableShifts(params: { line_bk: string; date_from: string; date_to: string }, token: string): Promise<NATSResponse> {
    return this.fetchChartData(StatementIds.LINE_AVAILABLE_SHIFTS, params as unknown as ChartRequestParams, token);
  }

  /**
   * Helper to extract data from response
   */
  static extractData<T = any>(response: NATSResponse<T>, fieldName?: string): T | null {
    if (!response.success || !response.data) {
      return null;
    }

    const results = response.data as any;
    
    if (fieldName) {
      return results[fieldName] || results;
    }

    return results as T;
  }

  /**
   * Helper to build request params from common chart props
   * Supports either machineSerialNo (machine-level) or lineId (line-level aggregated)
   */
  static buildRequestParams(
    machineSerialNo: string | undefined,
    dateRange: { start: string; end: string },
    shifts: string[],
    aggType?: 'shift' | 'day' | 'week',
    additionalParams?: Record<string, any>,
    lineId?: string
  ): ChartRequestParams {
    const baseParams: ChartRequestParams = {
      date_from: `${dateRange.start}T00:00:00.000Z`,
      date_to: `${dateRange.end}T23:59:59.999Z`,
      shift_name: shifts,
      ...(aggType ? { agg_type: aggType } : {}),
      ...(additionalParams || {})
    };
    
    // Use line_id for line-level data, serialno for machine-level data
    if (lineId) {
      baseParams.line_id = lineId;
    } else if (machineSerialNo) {
      baseParams.serialno = machineSerialNo;
    }
    
    return baseParams;
  }
}
